import * as React from 'react'
import { useMutation } from 'react-apollo'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons'
import { FormattedMessage, useIntl } from 'react-intl'
import { normalizePhoneNumber, pushGAEvent, HTMLEvent, normalizeSvensktOrganisationsnummer } from '../../../lib/utils'
import { ordersMutation } from '../../../views/signup-v3/dooerPreOffer'
import styles from './contactForm.module.scss'
import messages from './messages'
import { IContactFormProps, IFormValues } from './types'
import Inputs from './components/inputs'
import { useFormState } from 'react-use-form-state'
import { IFindCompanyCompany } from '../../find-company'
import { ratsitCompanyCodesToDooerCompanyCodes, companyRevenueToRevenueRange } from '../../../lib/signup-utils'

const ContactForm: React.FC<IContactFormProps> = ({ initialValues, variant = 'regular' }) => {
  // Form state
  const [formState, input] = useFormState<IFormValues>({
    companyBranch: null,
    companyStatus: null,
    companyForm: null,
    ...initialValues,
    companyEmployees:
      initialValues && initialValues.companyEmployees === 0
        ? undefined
        : initialValues && initialValues.companyEmployees,
  })

  // Company state
  const [company, setCompany] = React.useState<IFindCompanyCompany | null>()

  // Intl HOC
  const intl = useIntl()

  // State wether user submitted or not
  const [submitted, toggleSubmitted] = React.useState<boolean>(false)
  // Mutations
  const [createOrder, { loading: createOrderLoading, error: createOrderError }] = useMutation(ordersMutation)

  // Runs when submitting form
  const handleSubmit = (e: HTMLEvent) => {
    e.preventDefault()

    const {
      name,
      email,
      phone,
      bank,
      companyName,
      companyStatus,
      companyBranch,
      companyEmployees,
      companyRevenue,
      description,
    } = formState.values

    if (variant !== 'regular' && !company) {
      // eslint-disable-next-line no-alert
      return alert(intl.formatMessage(messages.alert))
    }

    createOrder({
      variables: {
        input: {
          contact: {
            name,
            email,
            phone: normalizePhoneNumber(phone),
          },
          organization: {
            name: companyName || (company && company.name) || name,
            seOrganizationNumber: (company && normalizeSvensktOrganisationsnummer(company.orgNr)) || null,
            city: (company && company.city) || null,
            employees: Number(companyEmployees),
            desc: description,
            branch: companyBranch,
            status:
              companyStatus || variant === 'companyRecentlyStarted'
                ? 'new'
                : variant === 'companyOld'
                ? 'active'
                : null,
            type: company ? ratsitCompanyCodesToDooerCompanyCodes[company.companyCode] : null,
            bank: bank || null,
            revenueRange: companyRevenueToRevenueRange(companyRevenue),
          },
        },
      },
    }).then(() => {
      formState.reset()
      toggleSubmitted(true)
      pushGAEvent('order-lead-accounting')
    })
  }

  return (
    <div className={styles.root}>
      {submitted ? (
        <div className={styles.submitted}>
          <i>
            <FontAwesomeIcon icon={faCheckCircle} />
          </i>
          <FormattedMessage {...messages.submittedHeading} tagName="h3" />
          <FormattedMessage {...messages.submittedLede} tagName="p" />
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <Inputs
            company={company}
            error={createOrderError}
            formState={formState}
            input={input}
            intl={intl}
            loading={createOrderLoading}
            setCompany={setCompany}
            variant={variant}
          />
        </form>
      )}
    </div>
  )
}

export default ContactForm
